import React, { useState } from 'react'
import { Classes, Dialog } from '@blueprintjs/core'
import { Flex, Icon, Toaster, WPGSelect } from '@ui'
import api, { IWorkplaceGroup } from '@api'
import { useFloor } from '@providers/mapProvider'
import css from './workplaceGroupPopup.module.scss'
import { SelectWrapper } from 'components/AdminFloorPlan/components/SelectWrapper'
import { Button } from '@runeffective/evo-design-system'
import { get } from 'lodash'
import EvoTreeSelect from 'components/EvoTreeSelect'

export interface ReallocateAllPopupDialogProps {
  setIsReallocateAllOpen: (para: boolean) => void
  isReallocateAllOpen: boolean
  selectedSpaces: string[]
}

export interface ObjectType {
  assignmentPlanId: string
  spaceIds: string[]
  toDepartmentId?: string | null
}

export default function ReallocateAllPopup({
  setIsReallocateAllOpen,
  isReallocateAllOpen,
  selectedSpaces,
}: ReallocateAllPopupDialogProps) {
  const [btnDisabled, setBtnDisabled] = useState(true)
  const [selectedDpt, setSelectedDpt] = useState(null as IWorkplaceGroup | null)
  const floor = useFloor()
  const apId = floor.apId

  function clearState() {
    setBtnDisabled(true)
    setSelectedDpt(null)
  }

  enum ActionType {
    ALLOCATE = 'allocate',
    UNALLOCATE = 'unallocate',
  }

  function onClickAllocateUnallocate(actionType: string) {
    let object: ObjectType = {
      assignmentPlanId: apId,
      spaceIds: selectedSpaces,
    }

    if (actionType === ActionType.ALLOCATE) {
      object.toDepartmentId = get(selectedDpt, 'id', null)
    }

    api.space.allocate(object).then(() => {
      let message = `${selectedSpaces.length} spaces were allocated to department ${get(
        selectedDpt,
        'name',
        null
      )}`
      if (actionType === ActionType.UNALLOCATE) {
        message = `${selectedSpaces.length} spaces were unallocated`
      }
      Toaster.success({ message: message })
      setIsReallocateAllOpen(false)
      floor.reloadMap?.()
    })
    clearState()
  }

  return (
    <>
      <Dialog
        style={{ paddingBottom: '0px' }}
        isOpen={isReallocateAllOpen}
        className={css.dialogWidth}
        transitionDuration={200}
        isCloseButtonShown={true}
        canOutsideClickClose={false}
        onClose={() => setIsReallocateAllOpen(false)}>
        <div className={css.empList}>
          <Button
            style={{ position: 'absolute', top: '5px', right: '10px' }}
            aria-label="single icon button"
            onClick={() => {
              setIsReallocateAllOpen(false)
              clearState()
            }}
            size="default"
            layout="borderless">
            <Icon name="close" />
          </Button>

          <div>
            <div className={css.dialogHeader}> Allocations </div>
            <div className={css.labelText}>
              Changing the department for the selected seats will unseat any seated employees that
              are part of this selection
            </div>
          </div>
          <div className={css.emptyDiv}>
            <div className={css.paddingTop}>
              <SelectWrapper title="Department allocation">
                <EvoTreeSelect
                  value={selectedDpt}
                  onChange={(department) => {
                    setSelectedDpt(department)
                    setBtnDisabled(false)
                  }}
                />
              </SelectWrapper>
            </div>
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <div className={css.emptyDiv}>
              <div className={css.row}>
                <div className={css.leftPanel}>
                  <Button
                    layout="danger"
                    onClick={() => {
                      setSelectedDpt(null)
                      onClickAllocateUnallocate(ActionType.UNALLOCATE)
                    }}
                    size="default">
                    Unallocate
                  </Button>
                </div>
                <div className={css.rightPanel}>
                  <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                    <Flex style={{ gap: '8px' }} direction="row" justify="space-between">
                      <Button
                        layout="borderless"
                        onClick={() => {
                          setIsReallocateAllOpen(false)
                          clearState()
                        }}
                        size="default">
                        Cancel
                      </Button>
                      <Button
                        disabled={btnDisabled}
                        layout="primary"
                        onClick={() => onClickAllocateUnallocate(ActionType.ALLOCATE)}
                        size="default">
                        Update
                      </Button>
                    </Flex>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  )
}
